import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import classNames from 'classnames';
import _ from 'lodash';
import appConfig from 'app-config';

import styles from './MLTooltipComponent.module.scss';


/*  This function translate <strong> and <p> tags found in the tooltip content into real tags
        Also, if there's a link, it will be added below.
     */
const isQnB = appConfig.portalCode === 'QB';
    
export function translateContent({ tooltipHeader, tooltipContent, tooltipHref, tooltipLink, tooltipContentClassName, tooltipHeaderClassName, noHeader, translator }) {
    if (!tooltipContent) {
        return '';
    }

    const translatedContent = translator(tooltipContent);
    const translatedLink = translator(tooltipLink);
    const translatedHeader = translator(tooltipHeader);

    if (!_.isUndefined(translatedContent.includes) && (translatedContent.includes('<strong>') || translatedContent.includes('<p>'))) {
        // Top to bottom -> p -> strong
        const splitByParagraph = translatedContent.split('<p>');
        return (
            <div className={tooltipContentClassName}>
                {!noHeader && tooltipHeader && (
                    <h3 className={!_.isUndefined(tooltipHeaderClassName) ? tooltipHeaderClassName : styles['tooltip-header']}>
                        {translatedHeader}
                    </h3>
                )}
                <div className={tooltipContentClassName}>
                    {splitByParagraph.map((paragraph, index) => {
                        let fixedParagraph = paragraph.replace('</p>', '');
                        const includesBr = fixedParagraph.includes('<br/>');
                        if (includesBr) {
                            fixedParagraph = fixedParagraph.replace('<br/>', '');
                        }
                        if (fixedParagraph.length === 0) {
                            // eslint-disable-next-line react/no-array-index-key
                            return (<React.Fragment key={`paragraph${index}`} />);
                        }
                        if (fixedParagraph.includes('<strong>')) {
                            const splitByStrong = fixedParagraph.split('<strong>');
                            return (
                                <p key={`paragraph${fixedParagraph}`}>
                                    {splitByStrong[0]}
                                    {splitByStrong.slice(1).map((boldText) => {
                                        const splitByEndingStrong = boldText.split('</strong>');
                                        return (
                                            <React.Fragment key={`fragment${boldText}`}>
                                                <strong key={`strong${boldText}`}>
                                                    {splitByEndingStrong[0]}
                                                </strong>
                                                {splitByEndingStrong[1]}
                                            </React.Fragment>
                                        );
                                    })}
                                </p>
                            );
                        }
                        return (
                            <>
                                <p className="vertical-spacing-10" key={`paragraph${fixedParagraph}`}>{fixedParagraph}</p>
                                {includesBr &&
                                    <br />
                                }
                            </>
                        );
                    })}
                </div>
                {(!_.isUndefined(tooltipHref) && !_.isUndefined(tooltipLink)) && <a href={tooltipHref} target="_blank" rel="noopener noreferrer">{translatedLink}</a>}
            </div>
        );
    }

    return (
        <React.Fragment>
            {!noHeader && tooltipHeader && (
                <h3 className={!_.isUndefined(tooltipHeaderClassName) ? tooltipHeaderClassName : styles['tooltip-header']}>
                    {translatedHeader}
                </h3>
            )}
            <div className={tooltipContentClassName}>
                {translatedContent}
            </div>          
            {!_.isUndefined(tooltipHref) && !_.isUndefined(tooltipLink) && <a href={tooltipHref} target="_blank" rel="noopener noreferrer">{translatedLink}</a>}
        </React.Fragment>
    );
}
function MLTooltipComponent(props) {
    const translator = useTranslator();

    const {
        tooltipContent,
        tooltipLabel,        
        tooltipLink,
        tooltipContentClassName,
        tooltipHeaderClassName,
        tooltipWrapperClassName,
        showLabel,
        noHeader,
        noWrapper
    } = props;

    let { tooltipHref, tooltipHeader } = props;

    if (!tooltipHeader && tooltipLabel && !noHeader) {
        tooltipHeader = tooltipLabel;
    }

    // For non local env's, tooltip links must start with /producer-engage
    if (tooltipHref && tooltipHref.indexOf('/') === 0       // href is relative path
        && tooltipHref.indexOf('/producer-engage') !== 0    // and href does not start with producer-engage
        && appConfig.portalCode === 'GPA'                   // and PE only
        && window.origin.indexOf('localhost') < 0) {        // and not local because local does not use /producer-engage path
        
        tooltipHref = `/producer-engage${tooltipHref}`        
    }

    const result = (
        <Tooltip
            id="tooltip"
            placement="bottom"
            trigger="click focus"
            content={translateContent({ tooltipHeader, tooltipContent, tooltipHref, tooltipLink, tooltipContentClassName, tooltipHeaderClassName, translator })}
            flipBehavior={['bottom', 'top']}
        >
            <span
                tabindex="0"
                className={classNames(
                    isQnB ? styles.tooltipLabelQB : styles.tooltipLabel,
                    {
                        [styles.displayBlockQB]:
                            isQnB && tooltipWrapperClassName?.includes('displayBlockQB'),
                    }
                )}
            >
                {tooltipLabel && showLabel ? translator(tooltipLabel) : null}
                <i className="mir mi-help-outline" />
            </span>
        </Tooltip>
    );

    if (noWrapper) {
        return result;
    }
    return (
        <div className={isQnB ? classNames(styles.tooltipTopMarginQB, tooltipWrapperClassName) : classNames(styles.tooltipTopMargin, tooltipWrapperClassName)}>
            {result}
        </div>
    );
}

MLTooltipComponent.propTypes = {
    tooltipContent: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            id: PropTypes.string,
            defaultMessage: PropTypes.string
        })]),
    tooltipLabel: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            id: PropTypes.string,
            defaultMessage: PropTypes.string
        })]),
    tooltipHref: PropTypes.string,
    tooltipLink: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            id: PropTypes.string,
            defaultMessage: PropTypes.string
        })]),
    tooltipContentClassName: PropTypes.string,
    tooltipHeaderClassName: PropTypes.string,
    tooltipWrapperClassName: PropTypes.string,
    /** tooltipHeader:
     *  if this field is empty and 'noHeader' is false,
     *  it will use 'tooltipLabel' as header.
     */
    tooltipHeader: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            id: PropTypes.string,
            defaultMessage: PropTypes.string
        })]),
    /** noHeader:
     * false: header will be rendered using 'tooltipHeader' as content (or 'tooltipLabel if empty).
     * true: header won't be rendered.
     */
    noHeader: PropTypes.bool,
    noWrapper: PropTypes.bool
};

MLTooltipComponent.defaultProps = {
    tooltipContent: undefined,
    tooltipLabel: undefined,
    tooltipHref: undefined,
    tooltipLink: undefined,
    tooltipContentClassName: undefined,
    tooltipHeaderClassName: undefined,
    tooltipWrapperClassName: undefined,
    tooltipHeader: undefined,
    showLabel: true,
    noHeader: false,
    noWrapper: false
};

export default MLTooltipComponent
